import { useAppSelector } from "@/app/hooks";
import { Property } from "@/features/survey/types";
import { getIconData } from "@/shared/utils/map";
import { isFieldVisible } from "@/shared/utils/propertyFieldRules";
import { getPropertyMarkerContent } from "@/shared/utils/propertyUtils";
import { EmeraldIcon } from "@emerald-react/icon";
import { EmeraldMenu } from "@emerald-react/menu";
import { ReactNode, useState } from "react";
import { Link, useParams } from "react-router";

interface MenuItem {
  path: string;
  label: string;
  isCustom?: boolean;
  children?: MenuItem[];
}

interface DropdownMenuProps {
  item: MenuItem;
  position?: "bottom_start" | "bottom_end" | "top_start" | "top_end";
}

// Helper to get property display name
const UNTITLED_PROPERTY = "Untitled Property";

const getPropertyDisplayName = (property: Property, isNameVisible: boolean): string => {
  const name = property.propertyName || property.name;
  const isUntitled = !name || name === UNTITLED_PROPERTY;

  if (!isNameVisible || isUntitled) {
    return property.address1 || (!isUntitled ? name! : undefined) || `Property ${property.id}`;
  }
  return name;
};

// Property icon/marker component
interface PropertyIconProps {
  property: Property;
  iconColor: string;
  markerContent: string | number;
}

const PropertyIcon = ({
  property,
  iconColor,
  markerContent,
}: PropertyIconProps) => {
  return (
    <div
      className="shrink-0 w-5 h-5 text-white rounded-full flex items-center justify-center text-xs font-semibold"
      style={{
        ...(property.isCurrentLocation
          ? { color: iconColor }
          : { backgroundColor: iconColor }),
      }}
    >
      {markerContent === "starIcon" ? (
        <EmeraldIcon icon="stars" type="filled" variant="icon" />
      ) : (
        markerContent
      )}
    </div>
  );
};

// Custom List Component
interface CustomListProps {
  children: ReactNode;
}

const CustomList = ({ children }: CustomListProps) => {
  return (
    <div className="bg-white rounded shadow-lg min-w-[200px] py-1 max-h-80 mdc-list overflow-y-auto">
      {children}
    </div>
  );
};

// Custom List Item Component
interface CustomListItemProps {
  children: ReactNode;
  onClick?: () => void;
  closeMenu?: () => void;
}

const CustomListItem = ({
  children,
  onClick,
  closeMenu,
}: CustomListItemProps) => {
  const handleClick = () => {
    if (onClick) onClick();
    if (closeMenu) closeMenu();
  };

  return (
    <div
      onClick={handleClick}
      className="hover:bg-gray-100 cursor-pointer transition-colors"
    >
      {children}
    </div>
  );
};

// Property item component with icon and marker
interface PropertyItemProps {
  property: Property;
  iconColor: string;
  markerContent: string | number;
  path: string;
  isNameVisible: boolean;
  closeMenu?: () => void;
}

const PropertyItem = ({
  property,
  iconColor,
  markerContent,
  path,
  isNameVisible,
  closeMenu,
}: PropertyItemProps) => {
  return (
    <Link to={path} className="block" onClick={closeMenu}>
      <div className="flex items-center space-x-3 px-4 py-2 hover:bg-gray-100 cursor-pointer">
        <PropertyIcon
          property={property}
          iconColor={iconColor}
          markerContent={markerContent}
        />
        <div className="flex-1 min-w-0">
          <div className="font-medium truncate text-sm text-gray-900">
            {getPropertyDisplayName(property, isNameVisible)}
          </div>
        </div>
      </div>
    </Link>
  );
};

// Properties submenu component
interface PropertiesSubmenuProps {
  child: MenuItem;
  filteredProperties: Property[];
  getIconColor: (property: Property) => string;
  getMenuMarkerContent: (property: Property) => string | number;
  isNameVisible: boolean;
  closeMenu?: () => void;
}

const PropertiesSubmenu = ({
  child,
  filteredProperties,
  getIconColor,
  getMenuMarkerContent,
  isNameVisible,
  closeMenu,
}: PropertiesSubmenuProps) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <CustomListItem>
      <div className="relative">
        <div
          className="flex items-center justify-between px-4 py-2 text-sm cursor-pointer"
          onClick={(e) => {
            e.stopPropagation();
            setIsOpen(!isOpen);
          }}
        >
          {child.label}
          <EmeraldIcon
            icon="arrow_drop_down"
            type="outlined"
            variant="symbol"
            className="ml-2"
          />
        </div>
        {isOpen && (
          <div className="top-0 flex flex-col bg-white z-50">
            {child.children?.map((property) => {
              const propertyData = filteredProperties.find(
                (p) => p.id.toString() === property.path.split("/").pop(),
              );

              if (propertyData) {
                const iconColor = getIconColor(propertyData);
                const markerContent = getMenuMarkerContent(propertyData);

                return (
                  <PropertyItem
                    key={property.path}
                    property={propertyData}
                    iconColor={iconColor}
                    markerContent={markerContent}
                    path={property.path}
                    isNameVisible={isNameVisible}
                    closeMenu={closeMenu}
                  />
                );
              }

              return (
                <Link
                  key={property.path}
                  to={property.path}
                  className="flex px-4 py-2 text-sm hover:bg-gray-100 cursor-pointer text-gray-900"
                  onClick={closeMenu}
                >
                  {property.label}
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </CustomListItem>
  );
};

// Regular menu item component
interface RegularMenuItemProps {
  child: MenuItem;
  closeMenu?: () => void;
}

const RegularMenuItem = ({ child, closeMenu }: RegularMenuItemProps) => {
  if (child.isCustom) {
    return (
      <CustomListItem
        onClick={() => {
          window.open(child.path, "_blank");
        }}
        closeMenu={closeMenu}
      >
        <div className="flex px-4 py-2 text-sm cursor-pointer">
          {child.label}
        </div>
      </CustomListItem>
    );
  }

  return (
    <CustomListItem closeMenu={closeMenu}>
      <Link to={child.path} className="block px-4 py-2 text-sm">
        {child.label}
      </Link>
    </CustomListItem>
  );
};

// Property list for top-level Properties dropdown
interface PropertyListProps {
  filteredProperties: Property[];
  getIconColor: (property: Property) => string;
  getMenuMarkerContent: (property: Property) => string | number;
  surveyId: string | undefined;
  isNameVisible: boolean;
  closeMenu?: () => void;
}

const PropertyList = ({
  filteredProperties,
  getIconColor,
  getMenuMarkerContent,
  surveyId,
  isNameVisible,
  closeMenu,
}: PropertyListProps) => {
  return (
    <>
      {filteredProperties.map((property) => (
        <CustomListItem key={property.id} closeMenu={closeMenu}>
          <PropertyItem
            property={property}
            iconColor={getIconColor(property)}
            markerContent={getMenuMarkerContent(property)}
            path={`/interactive/${surveyId}/property/${property.id}`}
            isNameVisible={isNameVisible}
            closeMenu={closeMenu}
          />
        </CustomListItem>
      ))}
    </>
  );
};

const DropdownMenu = ({ item, position }: DropdownMenuProps) => {
  const { surveyId } = useParams<{ surveyId: string }>();
  const { currentSurvey, mapFilters } = useAppSelector(
    (state) => state.interactiveSurvey,
  );
  const { showShortlistOnly } = mapFilters;
  const [submenuResetTrigger, setSubmenuResetTrigger] = useState(0);
  const [menuOpened, setMenuOpened] = useState(false);

  // Filter properties for dropdown based on map filters
  const modifiedProperties = !currentSurvey?.properties
    ? []
    : currentSurvey.properties.filter((property) => {
        if (property.isHidden) return false;
        if (property.isCurrentLocation && !currentSurvey.showCurrentLocationPD)
          return false;
        if (showShortlistOnly && !property.isClientShortlist) return false;
        return true;
      });
  const filteredProperties = showShortlistOnly
    ? modifiedProperties.filter((p) => p.isClientShortlist)
    : modifiedProperties;

  // Helper function to get icon color using getIconData
  const getIconColor = (property: Property) => getIconData(property).iconColor;

  // Get marker content similar to PropertyHeader's getPropertyOrderIcon
  const getMenuMarkerContent = (property: Property) => {
    const orderIndex = getPropertyMarkerContent(property, filteredProperties);
    return orderIndex;
  };

  // Determine if this is a property menu by checking label
  const isPropertyMenu = item.label === "Properties" && item.children;
  const isMoreMenu = item.label === "More" && item.children;

  // Check if the name field rule is visible; fall back to true when no rules configured
  const hasFieldRules =
    !!currentSurvey?.surveyPropertyFieldRule?.propertyFieldRuleList?.length;
  const isNameVisible = hasFieldRules
    ? isFieldVisible(currentSurvey, "name")
    : true;

  // Determine menu position
  const menuPosition = position || (isMoreMenu ? "bottom_end" : "bottom_start");

  // For property menu, get property data from children paths
  const propertyDataFromChildren =
    isPropertyMenu && item.children
      ? item.children
          .map((child) => {
            const propertyId = child.path.split("/").pop();
            return filteredProperties.find(
              (p) => p.id.toString() === propertyId,
            );
          })
          .filter((p): p is Property => p !== undefined)
      : [];

  const closeMenu = () => setMenuOpened(false);

  return (
    <EmeraldMenu
      position={menuPosition}
      className="header-more-menu"
      opened={menuOpened}
      onClose={() => {
        setMenuOpened(false);
        setSubmenuResetTrigger((prev) => prev + 1);
      }}
      triggerElement={
        <div
          className="nav-item whitespace-nowrap flex h-18 items-center text-white px-4"
          onClick={() => setMenuOpened(!menuOpened)}
        >
          {item.label}{" "}
          <EmeraldIcon
            icon="arrow_drop_down"
            type="outlined"
            variant="symbol"
            className="inline-block"
          />
        </div>
      }
    >
      <CustomList>
        {isPropertyMenu ? (
          <PropertyList
            filteredProperties={propertyDataFromChildren}
            getIconColor={getIconColor}
            getMenuMarkerContent={getMenuMarkerContent}
            surveyId={surveyId}
            isNameVisible={isNameVisible}
            closeMenu={closeMenu}
          />
        ) : (
          item.children?.map((child) => {
            if (child.label === "Properties" && child.children) {
              return (
                <PropertiesSubmenu
                  key={`${child.label}-${submenuResetTrigger}`}
                  child={child}
                  filteredProperties={filteredProperties}
                  getIconColor={getIconColor}
                  getMenuMarkerContent={getMenuMarkerContent}
                  isNameVisible={isNameVisible}
                  closeMenu={closeMenu}
                />
              );
            }
            return (
              <RegularMenuItem
                key={child.path}
                child={child}
                closeMenu={closeMenu}
              />
            );
          })
        )}
      </CustomList>
    </EmeraldMenu>
  );
};

export default DropdownMenu;
